import { supabase, isConfigured } from './supabase';
import type { Task, TaskStatus, TaskPriority } from '../types';

export interface CreateTaskInput {
  team_id: string;
  contact_id?: string | null;
  deal_id?: string | null;
  owner_user_id?: string | null;
  title: string;
  due_at: string;
  status?: TaskStatus;
  priority?: TaskPriority;
}

export interface UpdateTaskInput extends Partial<Omit<CreateTaskInput, 'team_id'>> {
  id: string;
}

export async function getTasks(
  teamId: string,
  options?: { allTeams?: boolean }
): Promise<Task[]> {
  if (!isConfigured) return [];

  let query = supabase
    .from('sales_tasks')
    .select(`
      *,
      contact:sales_contacts(id, first_name, last_name, company),
      deal:sales_deals(id, title)
    `)
    .order('due_at', { ascending: true });

  if (!options?.allTeams) {
    query = query.eq('team_id', teamId);
  }

  const { data, error } = await query;

  if (error) {
    console.error('[tasks] fetch failed:', error);
    return [];
  }

  return data as Task[];
}

export async function getTasksForDeal(dealId: string): Promise<Task[]> {
  if (!isConfigured) return [];

  const { data, error } = await supabase
    .from('sales_tasks')
    .select(`
      *,
      contact:sales_contacts(id, first_name, last_name, company)
    `)
    .eq('deal_id', dealId)
    .order('due_at', { ascending: true });

  if (error) {
    console.error('[tasks] fetch for deal failed:', error);
    return [];
  }

  return data as Task[];
}

export async function createTask(input: CreateTaskInput): Promise<Task | null> {
  if (!isConfigured) return null;

  const { data, error } = await supabase
    .from('sales_tasks')
    .insert(input)
    .select(`
      *,
      contact:sales_contacts(id, first_name, last_name, company),
      deal:sales_deals(id, title)
    `)
    .single();

  if (error) {
    console.error('[tasks] create failed:', error);
    return null;
  }

  return data as Task;
}

export async function updateTask(input: UpdateTaskInput): Promise<Task | null> {
  if (!isConfigured) return null;

  const { id, ...updates } = input;
  const { data, error } = await supabase
    .from('sales_tasks')
    .update(updates)
    .eq('id', id)
    .select(`
      *,
      contact:sales_contacts(id, first_name, last_name, company),
      deal:sales_deals(id, title)
    `)
    .single();

  if (error) {
    console.error('[tasks] update failed:', error);
    return null;
  }

  return data as Task;
}

export async function markTaskDone(id: string): Promise<boolean> {
  if (!isConfigured) return false;

  const { error } = await supabase
    .from('sales_tasks')
    .update({ status: 'done' })
    .eq('id', id);

  if (error) {
    console.error('[tasks] mark done failed:', error);
    return false;
  }

  return true;
}

export async function markTaskOpen(id: string): Promise<boolean> {
  if (!isConfigured) return false;

  const { error } = await supabase
    .from('sales_tasks')
    .update({ status: 'open' })
    .eq('id', id);

  if (error) {
    console.error('[tasks] mark open failed:', error);
    return false;
  }

  return true;
}

export async function deleteTask(id: string): Promise<boolean> {
  if (!isConfigured) return false;

  const { error } = await supabase
    .from('sales_tasks')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('[tasks] delete failed:', error);
    return false;
  }

  return true;
}
